import { PrescriptionDisplayData, PrescriptionResponse } from '@ca/common'
import { useState } from 'react'
import { View } from 'react-native'
import Animated from 'react-native-reanimated'

import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'

import { Collapsible } from '@ui/animated/Collapsible'
import Button from '@ui/buttons/Button'
import { colors } from '@ui/colors'
import { PdSection, useResolvedPd } from '@order/PdSection'
import PrescriptionHead from '@screens/vision/prescription/view/PrescriptionHead'
import Comment from '@screens/vision/prescription/view/sections/Comment'
import Prism from '@screens/vision/prescription/view/sections/Prism'
import Refraction from '@screens/vision/prescription/view/sections/Refraction'
import { useTranslation } from 'react-i18next'
import {
  HEIGHT_PRESCRIPTION_ITEM,
  HEIGHT_PRESCRIPTION_VALUES,
} from '../prescription/view/style'
import Icon from '@ui/elements/Icon'
import Skeleton from '@ui/elements/Skeleton'
import { MeasurementLocation } from 'hooks/measurements/useMeasureSdk'
import cn from '@ui/utils/cn'

interface ValuesProps {
  data: PrescriptionDisplayData
  collapsible?: boolean
  showHeader?: boolean
}
export function Values({
  data,
  collapsible = true,
  showHeader = true,
}: ValuesProps) {
  const { t } = useTranslation(['prescription'])
  const [expandComment, setExpandComment] = useState(false)
  const [expandPrism, setExpandPrism] = useState(false)

  const pdOverride = useResolvedPd()

  const isPrism = data.prism
  const isComment = !!data.remark

  return (
    <View
      className={cn(
        'relative inset-0 flex flex-col gap-0',
        collapsible && 'rounded-xl border border-black/10'
      )}
    >
      {showHeader && (
        <PrescriptionHead data={data}>
          <View className="flex h-8 w-8 items-center justify-center">
            <Icon name="check" />
          </View>
        </PrescriptionHead>
      )}
      <View className={cn('pb-4', showHeader && 'border-t border-black/10')}>
        <Refraction data={data} pdOverride={pdOverride || undefined} />
      </View>
      <Collapsible
        fullWidth
        animated={collapsible}
        className="pointer-events-none"
        open={collapsible ? expandPrism : isPrism}
      >
        <View className="border-t border-black/10 pt-4">
          <Prism data={data} />
        </View>
      </Collapsible>
      <Collapsible
        fullWidth
        animated={collapsible}
        className="pointer-events-none"
        open={collapsible ? expandComment : isComment}
      >
        <View className="border-t border-black/10 pt-4">
          <Comment data={data} />
        </View>
      </Collapsible>
      {collapsible && (
        <View
          className="absolute flex h-8 w-full flex-row items-center justify-center gap-8 px-8"
          style={{ left: 0, top: HEIGHT_PRESCRIPTION_ITEM }}
        >
          <Button
            overrideColorBack={isPrism ? colors.white : undefined}
            size="xs"
            variant="outlined"
            iconEnd={
              isPrism ? (expandPrism ? 'chevron-up' : 'chevron-down') : 'minus'
            }
            label={t('prescription:prism')}
            disabled={!isPrism}
            onPress={() => setExpandPrism((prev) => !prev)}
          />
          <Button
            overrideColorBack={isComment ? colors.white : undefined}
            size="xs"
            variant="outlined"
            iconEnd={
              isComment
                ? expandComment
                  ? 'chevron-up'
                  : 'chevron-down'
                : 'minus'
            }
            label={t('prescription:comment')}
            disabled={!isComment}
            onPress={() => setExpandComment((prev) => !prev)}
          />
        </View>
      )}
    </View>
  )
}

export default function Wrapper({
  prescription,
  isLoading,
}: {
  prescription?: PrescriptionResponse
  isLoading?: boolean
}) {
  const styleLoaded = useAnimatedOpacity(!!prescription)

  // if (isLoading) return <LoadingFooter />
  if (isLoading)
    return (
      <View className="flex flex-col gap-8 px-8">
        <Skeleton
          className="w-full rounded-xl"
          style={{
            height: HEIGHT_PRESCRIPTION_VALUES,
            backgroundColor: colors.black10,
          }}
        />
        <Skeleton
          className="w-full rounded-xl"
          style={{
            height: HEIGHT_PRESCRIPTION_ITEM,
            backgroundColor: colors.black10,
          }}
        />
      </View>
    )

  return (
    <Animated.View className="flex flex-col gap-8 px-8" style={styleLoaded}>
      {prescription && <Values data={prescription} />}
      {prescription && <PdSection location={MeasurementLocation.vision} />}
    </Animated.View>
  )
}
